import React, { useState, useRef } from 'react';
import { processFile } from '../services/fileService';

function FileUpload({ onFilesSelected, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = async (files) => {
    setProcessing(true);
    
    try {
      const processedFiles = await Promise.all(
        files.map(file => processFile(file))
      );

      setSelectedFiles(processedFiles);
      onFilesSelected(processedFiles);
    } catch (error) {
      console.error('File processing error:', error);
      alert('Error processing files');
    } finally {
      setProcessing(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="file-upload-container">
      {processing && (
        <div className="processing-indicator">Processing files...</div>
      )}
      
      {selectedFiles.length > 0 ? (
        <div className="selected-files">
          {selectedFiles.map((file, idx) => (
            <div key={idx} className="file-item">
              {file.isImage && (
                <img src={file.preview} alt={file.name} className="file-preview-img" />
              )}
              <div className="file-info">
                <div className="file-name">
                  {file.name}
                  {file.truncated && (
                    <span className="truncated-badge" title="File content truncated to 2000 chars">
                      ✂️ Truncated
                    </span>
                  )}
                  {file.note && (
                    <span className="note-badge" title={file.note}>
                      ℹ️
                    </span>
                  )}
                </div>
                <div className="file-size">{formatFileSize(file.size)}</div>
                {file.fullContent && (
                  <div className="file-stats">
                    Full: {file.fullContent.length} chars | 
                    Sending: {file.content?.length || 0} chars
                  </div>
                )}
                {file.note && (
                  <div className="file-note">{file.note}</div>
                )}
              </div>
              <button 
                className="remove-file-btn"
                onClick={() => removeFile(idx)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div 
          className={`drop-zone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="drop-zone-content">
            <div className="drop-zone-icon">📎</div>
            <div className="drop-zone-text">
              Drop files here or click to upload
            </div>
            <div className="drop-zone-hint">
              ✅ Images • TXT • JSON • Markdown
            </div>
            <div className="drop-zone-hint">
              📦 PDF • Excel (.xlsx) • Word (.docx)
            </div>
            <div className="drop-zone-note">
              ⚠️ Text files &gt;2000 chars will be truncated
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
        accept="image/*,.pdf,.txt,.json,.md,.xlsx,.xls,.docx,.doc"
        disabled={disabled || processing}
      />
    </div>
  );
}

export default FileUpload;
